import axios, { AxiosInstance, AxiosResponse } from "axios";
import { ProfileFormData } from "../types/form";

// Single source of truth for API configuration
const API_BASE_URL = process.env.REACT_APP_BASE_API_URL || "";

let authToken: string | null = null;
// Create single axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${authToken}`,
  },
});

// --- Auth token attachment helpers ---
export const setAuthToken = (token: string | null) => {
  authToken = token;
};

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    if (authToken) {
      config.headers = config.headers || {};
      (config.headers as Record<string, string>)["Authorization"] =
        `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    console.error("API Response Error:", error);
    return Promise.reject(error);
  },
);

// Centralized error handler
const handleAxiosError = (error: unknown, defaultMessage: string): string => {
  let errorMessage = defaultMessage;

  if (axios.isAxiosError(error)) {
    if (error.response) {
      // Server responded with error status
      const errorData = error.response.data;
      if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      } else if (typeof errorData === "string") {
        errorMessage = errorData;
      } else {
        errorMessage = `HTTP error! status: ${error.response.status}`;
      }
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = "No response received from server";
    } else {
      // Something else happened
      errorMessage = error.message;
    }
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }

  return errorMessage;
};

export interface ApiResponse {
  success: boolean;
  token?: string;
  appLink?: string;
  message?: string;
  error?: string;
  profileProperties?: any;
}

export interface StripeCheckoutResponse {
  clientSecret: string;
}

export interface PaymentStatusResponse {
  is_successful: boolean;
  payment_status: {
    status: string;
    customer_email?: string;
  };
}

export interface ProfileDraftResponse {
  success: boolean;
  message?: string;
  error?: string | "EMAIL_EXISTS" | "ORGANIZATION_EXISTS";
  profileId?: string;
}

// Save profile draft to server
export const saveProfileDraft = async (
  formData: ProfileFormData,
): Promise<ProfileDraftResponse> => {
  try {
    // Only save if email is provided
    if (!formData.email) {
      return {
        success: false,
        error: "Email is required to save profile draft",
      };
    }

    // If authenticated (editing mode), ensure isEditing flag is set
    const formDataWithEditing: ProfileFormData = authToken
      ? { ...formData, isEditing: true }
      : formData;

    // Prepare FormData for file uploads
    const submitData = new FormData();

    // Add all text data as JSON string in 'profileData' field
    const profileDataString = JSON.stringify(formDataWithEditing);
    submitData.append("profileData", profileDataString);

    // Add files separately with their specific field names
    if (
      formDataWithEditing.headshot &&
      formDataWithEditing.headshot instanceof File
    ) {
      submitData.append("headshot", formDataWithEditing.headshot);
    }
    if (
      formDataWithEditing.businessLogo &&
      formDataWithEditing.businessLogo instanceof File
    ) {
      submitData.append("businessLogo", formDataWithEditing.businessLogo);
    }
    if (
      formDataWithEditing.qrCodeLogo &&
      formDataWithEditing.qrCodeLogo instanceof File
    ) {
      submitData.append("qrCodeLogo", formDataWithEditing.qrCodeLogo);
    }
    if (
      formDataWithEditing.fabButtonLogo &&
      formDataWithEditing.fabButtonLogo instanceof File
    ) {
      submitData.append("fabButtonLogo", formDataWithEditing.fabButtonLogo);
    }
    if (
      formDataWithEditing.faviconLogo &&
      formDataWithEditing.faviconLogo instanceof File
    ) {
      submitData.append("faviconLogo", formDataWithEditing.faviconLogo);
    }

    // Add review images with the expected naming pattern
    if (formDataWithEditing.reviews) {
      formDataWithEditing.reviews.forEach((review, index) => {
        if (review.image && review.image instanceof File) {
          submitData.append(`reviewImage_${index + 1}`, review.image);
        }
      });
    }

    const response = await apiClient.post(
      "/api/organization/saveProfile",
      submitData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const result = response.data;

    return {
      success: true,
      message: "Profile draft saved successfully",
      profileId: result.profileId,
    };
  } catch (error) {
    console.error("Profile Draft Save Error:", error);

    const errorMessage = handleAxiosError(
      error,
      "Failed to save profile draft",
    );

    // Check if this is an organization already exists error
    if (errorMessage.includes("Organization with this email already exists")) {
      return {
        success: false,
        error: "ORGANIZATION_EXISTS",
        message: errorMessage,
      };
    }

    // Check if this is a general email already exists error
    if (
      errorMessage.includes("email already exists") ||
      errorMessage.includes("already exists")
    ) {
      return {
        success: false,
        error: "EMAIL_EXISTS",
        message: errorMessage,
      };
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Check if profile exists for email (before saving)
export const checkExistingProfile = async (
  email: string,
): Promise<ProfileFormData | null> => {
  try {
    const response = await apiClient.get(
      `/api/organization/getProfile?email=${encodeURIComponent(email)}`,
    );

    // Only return data if it's a valid profile object with meaningful content
    if (
      response.data &&
      typeof response.data === "object" &&
      Object.keys(response.data).length > 0
    ) {
      // Additional validation: ensure it has at least basic profile fields
      if (
        response.data.email ||
        response.data.firstName ||
        response.data.lastName
      ) {
        return response.data;
      }
    }

    return null;
  } catch (error) {
    // Silent error handling - only log in development
    if (process.env.NODE_ENV === "development") {
      console.error("Error checking existing profile:", error);
    }
    return null;
  }
};

// Fetch existing profile for completion
export const fetchExistingProfile = async (
  email: string,
): Promise<ProfileFormData | null> => {
  try {
    const response = await apiClient.get(
      `/api/organization/getProfile?email=${encodeURIComponent(email)}`,
    );

    // Only return data if it's a valid profile object with meaningful content
    if (
      response.data &&
      typeof response.data === "object" &&
      Object.keys(response.data).length > 0
    ) {
      // Additional validation: ensure it has at least basic profile fields
      if (
        response.data.email ||
        response.data.firstName ||
        response.data.lastName
      ) {
        return response.data;
      }
    }

    return null;
  } catch (error) {
    // Silent error handling - only log in development
    if (process.env.NODE_ENV === "development") {
      console.error("Error fetching existing profile:", error);
    }
    return null;
  }
};

export const submitProfile = async (
  formData: ProfileFormData,
): Promise<ApiResponse> => {
  try {
    // If authenticated (editing mode), ensure isEditing flag is set
    const formDataWithEditing: ProfileFormData = authToken
      ? { ...formData, isEditing: true }
      : formData;

    // Prepare FormData for file uploads
    const submitData = new FormData();

    // Add all text data as JSON string in 'profileData' field (as expected by server)
    submitData.append("profileData", JSON.stringify(formDataWithEditing));

    // Add files separately with their specific field names
    if (
      formDataWithEditing.headshot &&
      formDataWithEditing.headshot instanceof File
    ) {
      submitData.append("headshot", formDataWithEditing.headshot);
    }
    if (
      formDataWithEditing.businessLogo &&
      formDataWithEditing.businessLogo instanceof File
    ) {
      submitData.append("businessLogo", formDataWithEditing.businessLogo);
    }
    if (
      formDataWithEditing.qrCodeLogo &&
      formDataWithEditing.qrCodeLogo instanceof File
    ) {
      submitData.append("qrCodeLogo", formDataWithEditing.qrCodeLogo);
    }
    if (
      formDataWithEditing.fabButtonLogo &&
      formDataWithEditing.fabButtonLogo instanceof File
    ) {
      submitData.append("fabButtonLogo", formDataWithEditing.fabButtonLogo);
    }
    if (
      formDataWithEditing.faviconLogo &&
      formDataWithEditing.faviconLogo instanceof File
    ) {
      submitData.append("faviconLogo", formDataWithEditing.faviconLogo);
    }

    // Add review images with the expected naming pattern (reviewImage_1, reviewImage_2, etc.)
    if (formDataWithEditing.reviews) {
      formDataWithEditing.reviews.forEach((review, index) => {
        if (review.image && review.image instanceof File) {
          submitData.append(`reviewImage_${index + 1}`, review.image);
        }
      });
    }

    // check if user has paid
    const paymentResult = await checkPaymentHistory(formDataWithEditing.email);
    const hasPaid = paymentResult.has_payments;
    const hasSucceededPayment = paymentResult.payments.some((payment) => payment.status === "succeeded");
    if (!hasPaid || !hasSucceededPayment) {
      window.location.href = "/";
      return {
        success: false,
        error: "Payment required",
      };
    }

    // Choose endpoint based on authentication status
    const endpoint = authToken
      ? "/api/organization/updateProfile"
      : "/api/organization/createProfile";

    const response = await apiClient.post(endpoint, submitData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const result = response.data;

    return {
      success: true,
      appLink: result.appLink,
      message: "Profile created successfully!",
    };
  } catch (error) {
    console.error("API Error:", error);

    const errorMessage = handleAxiosError(error, "An unknown error occurred");

    return {
      success: false,
      error: errorMessage,
    };
  }
};

export const loadQRCode = async (topicId: string): Promise<string | null> => {
  return `${API_BASE_URL}/api/exhibit/qr?id=${topicId}`;
};

export const createStripeCheckoutSession = async (
  priceId: string,
  productId: string,
  currency: string = "usd",
): Promise<string> => {
  try {
    const response = await apiClient.post<StripeCheckoutResponse>(
      "/api/organization/stripeCheckout",
      {
        price_id: priceId,
        currency: currency,
        product_id: productId,
      },
    );

    const data = response.data;

    if (!data.clientSecret) {
      throw new Error("No client secret received from server");
    }

    return data.clientSecret;
  } catch (error) {
    console.error("Stripe Checkout Error:", error);

    const errorMessage = handleAxiosError(
      error,
      "Failed to create checkout session",
    );
    throw new Error(errorMessage);
  }
};

export const checkPaymentStatus = async (
  sessionId: string,
): Promise<PaymentStatusResponse> => {
  try {
    const response = await apiClient.post<PaymentStatusResponse>(
      "/api/organization/paymentStatus",
      { session_id: sessionId },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );

    const data = response.data;

    if (!data.is_successful || !data.payment_status) {
      throw new Error("Invalid payment status response");
    }

    return data;
  } catch (error) {
    console.error("Payment Status Error:", error);

    const errorMessage = handleAxiosError(
      error,
      "Failed to check payment status",
    );
    throw new Error(errorMessage);
  }
};

export const login = async (
  email: string,
  password: string,
): Promise<ApiResponse> => {
  try {
    const response = await apiClient.post<ApiResponse>("/api/auth/login", {
      email,
      password,
    });
    return {
      success: true,
      message: "Login successful!",
      token: String(response.data),
    };
  } catch (error) {
    console.error("Login Error:", error);
    const errorMessage = handleAxiosError(error, "An unknown error occurred");
    return {
      success: false,
      error: errorMessage,
      message: "Login failed",
    };
  }
};

export const getLocation = async (email: string): Promise<ApiResponse> => {
  try {
    const response = await apiClient.post<ApiResponse>(
      "/api/location/getLocationsByEmail",
      {
        email: email,
      },
    );
    if (
      response?.data &&
      Array.isArray(response?.data) &&
      response?.data?.length > 0
    ) {
      const org = await getOrg(email);

      // If referralName exists in org data, set wasReferred to true
      const hasReferralName =
        org?.referralName && org.referralName.trim() !== "";
      const profileProperties = {
        ...response?.data?.[0]?.profileProperties,
        referralName: org?.referralName,
        wasReferred: hasReferralName
          ? true
          : response?.data?.[0]?.profileProperties?.wasReferred,
      };
      return {
        success: true,
        message: "Location fetched successfully",
        profileProperties: profileProperties,
      };
    } else {
      return {
        success: false,
        error: "An unknown error occurred",
        message: "Failed to get location",
      };
    }
  } catch (error) {
    console.error("Location Error:", error);
  }
  return {
    success: false,
    error: "An unknown error occurred",
    message: "Failed to get location",
  };
};

export const getOrg = async (email: string) => {
  try {
    const response = await apiClient.post(`/api/organization/getByEmail`, {
      email: email,
    });
    return response.data;
  } catch (error) {
    console.error("Organization Error:", error);
  }
};

// Check payment history for an email
export const checkPaymentHistory = async (email: string): Promise<{
  success: boolean;
  has_payments: boolean;
  payment_count: number;
  payments: Array<{
    payment_intent_id: string;
    amount: number;
    currency: string;
    status: string;
    created: number;
    customer_email: string;
    description: string;
    receipt_email: string | null;
  }>;
  message: string;
}> => {
  try {
    const response = await apiClient.post("/api/organization/confirmPayment", {
      email: email,
    });
    return response.data;
  } catch (error) {
    console.error("Payment History Check Error:", error);
    return {
      success: false,
      has_payments: false,
      payment_count: 0,
      payments: [],
      message: "Failed to check payment history",
    };
  }
};

// Check email availability
export const checkEmailAvailability = async (email: string): Promise<{
  email: string;
  isAvailable: boolean;
  organizationExists: boolean;
  profileExists: boolean;
  message: string;
}> => {
  try {
    const response = await apiClient.get(
      `/api/organization/checkEmailAvailability?email=${encodeURIComponent(email)}`
    );
    return response.data;
  } catch (error) {
    console.error("Email Availability Check Error:", error);
    return {
      email: email,
      isAvailable: false,
      organizationExists: false,
      profileExists: false,
      message: "Failed to check email availability",
    };
  }
};
